import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, X } from "lucide-react";
import { formatPrice } from "@/lib/currency";

const CATS = ["clothing", "hijabs", "bags", "accessories"];

const Tab = ({ active, children, onClick }) => (
  <button onClick={onClick} className={`text-[0.7rem] tracking-[0.25em] uppercase pb-2 border-b ${active ? "border-[#C5A059] text-[#0A0A0A]" : "border-transparent text-[#5A5A5A]"}`}>{children}</button>
);

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [bags, setBags] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [showAdd, setShowAdd] = useState(false);

  const refresh = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/orders").then((r) => setOrders(r.data));
    api.get("/custom-bags").then((r) => setBags(r.data)).catch(() => {});
    api.get("/coupons").then((r) => setCoupons(r.data)).catch(() => {});
  };

  useEffect(() => { if (user?.role === "admin") refresh(); }, [user]);

  if (loading) return <div className="text-center py-32 overline">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div data-testid="admin-page" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
      <div className="overline text-[#C5A059]">Admin</div>
      <h1 className="serif text-5xl mt-3">Dashboard</h1>

      <div className="flex gap-8 mt-10 mb-10 border-b border-[#E5E5DF] overflow-x-auto">
        {["overview", "products", "orders", "bespoke", "coupons"].map((t) => (
          <Tab key={t} active={tab === t} onClick={() => setTab(t)}>
            <span data-testid={`admin-tab-${t}`}>{t}</span>
          </Tab>
        ))}
      </div>

      {tab === "overview" && stats && (
        <div data-testid="admin-overview">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { l: "Revenue", v: formatPrice(stats.revenue, { decimals: 0 }) },
              { l: "Orders", v: stats.total_orders },
              { l: "Products", v: stats.total_products },
              { l: "Customers", v: stats.total_users },
            ].map((k) => (
              <div key={k.l} className="border border-[#E5E5DF] p-6 bg-white">
                <div className="overline">{k.l}</div>
                <div className="serif text-4xl mt-3">{k.v}</div>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <h2 className="serif text-2xl mb-4">Recent orders</h2>
            <div className="space-y-2">
              {stats.recent_orders.map((o) => (
                <div key={o.id} className="border border-[#E5E5DF] p-4 flex justify-between text-sm">
                  <span>#{o.id.slice(0,8)}</span>
                  <span>{formatPrice(o.total)}</span>
                  <span className="text-[#5A5A5A]">{o.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "products" && (
        <div data-testid="admin-products">
          <div className="flex justify-between mb-6">
            <h2 className="serif text-2xl">Catalog ({products.length})</h2>
            <button onClick={() => setShowAdd(true)} data-testid="admin-add-product" className="btn-primary"><Plus size={14} /> Add product</button>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {products.map((p) => (
              <div key={p.id} className="border border-[#E5E5DF] p-4 flex items-center gap-4 bg-white">
                <img src={p.images?.[0]} alt="" className="w-14 h-14 object-cover" />
                <div className="flex-1">
                  <div className="serif text-lg">{p.name}</div>
                  <div className="text-xs text-[#5A5A5A]">{p.category} · stock {p.stock}</div>
                </div>
                <div className="text-sm">{formatPrice(p.price, { decimals: 0 })}</div>
                <button
                  data-testid={`admin-delete-${p.id}`}
                  onClick={async () => { await api.delete(`/products/${p.id}`); toast.success("Deleted"); refresh(); }}
                  className="text-red-700"
                ><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
          {showAdd && <AddProductModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); refresh(); }} />}
        </div>
      )}

      {tab === "orders" && (
        <div data-testid="admin-orders">
          <h2 className="serif text-2xl mb-6">All orders ({orders.length})</h2>
          <div className="space-y-2">
            {orders.map((o) => (
              <div key={o.id} className="border border-[#E5E5DF] p-4 bg-white">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="serif text-lg">#{o.id.slice(0,8)} · {o.shipping_info.name}</div>
                    <div className="text-xs text-[#5A5A5A]">{o.items.length} items · {formatPrice(o.total)} · {new Date(o.created_at).toLocaleDateString()}</div>
                  </div>
                  <select
                    value={o.status}
                    onChange={async (e) => { await api.patch(`/orders/${o.id}/status`, null, { params: { status: e.target.value } }); refresh(); }}
                    className="border border-[#E5E5DF] px-3 py-1 text-xs"
                  >
                    {["pending","processing","shipped","delivered","cancelled"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "bespoke" && (
        <div data-testid="admin-bespoke">
          <h2 className="serif text-2xl mb-6">Bespoke requests ({bags.length})</h2>
          <div className="space-y-2">
            {bags.map((b) => (
              <div key={b.id} className="border border-[#E5E5DF] p-4 bg-white">
                <div className="serif text-lg">{b.contact_name} · {b.style}</div>
                <div className="text-xs text-[#5A5A5A]">{b.material} · {b.color} · {b.size} · {b.hardware} {b.initials && `· "${b.initials}"`}</div>
                <div className="text-xs mt-1">{b.contact_email}</div>
                {b.special_instructions && <div className="text-xs italic mt-2">"{b.special_instructions}"</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "coupons" && (
        <div data-testid="admin-coupons">
          <CouponEditor coupons={coupons} refresh={refresh} />
        </div>
      )}
    </div>
  );
}

function CouponEditor({ coupons, refresh }) {
  const [code, setCode] = useState("");
  const [pct, setPct] = useState(10);
  const add = async (e) => {
    e.preventDefault();
    if (!code) return;
    try { await api.post("/coupons", { code, discount_percent: pct, active: true }); toast.success("Created"); setCode(""); refresh(); }
    catch { toast.error("Coupon code exists"); }
  };
  return (
    <>
      <h2 className="serif text-2xl mb-6">Coupons</h2>
      <form onSubmit={add} className="flex gap-3 mb-8">
        <input data-testid="coupon-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="CODE" className="fld" />
        <input data-testid="coupon-pct-input" type="number" min="1" max="90" value={pct} onChange={(e) => setPct(+e.target.value)} className="fld w-28" />
        <button data-testid="coupon-create" className="btn-primary">Add</button>
      </form>
      <div className="space-y-2">
        {coupons.map((c) => (
          <div key={c.id} className="border border-[#E5E5DF] p-4 bg-white flex justify-between items-center">
            <div className="font-mono">{c.code} · {c.discount_percent}%</div>
            <button onClick={async () => { await api.delete(`/coupons/${c.id}`); refresh(); }} data-testid={`coupon-delete-${c.code}`} className="text-red-700"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </>
  );
}

function AddProductModal({ onClose, onSaved }) {
  const [f, setF] = useState({
    name: "", category: "clothing", price: 99, description: "", material: "",
    images: "", sizes: "S,M,L", colors: "Black,Bone", stock: 10, is_new: false, is_bestseller: false,
  });
  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...f, price: +f.price, stock: +f.stock,
      images: f.images.split(",").map((s) => s.trim()).filter(Boolean),
      sizes: f.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      colors: f.colors.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try { await api.post("/products", payload); toast.success("Product created"); onSaved(); }
    catch { toast.error("Failed to create"); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-[80] flex items-center justify-center p-6">
      <form onSubmit={save} className="bg-[#F5F5F0] w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between mb-6"><div className="serif text-2xl">New product</div><button type="button" onClick={onClose}><X /></button></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><div className="fld-label">Name</div><input data-testid="new-product-name" required value={f.name} onChange={(e) => setF({...f, name: e.target.value})} className="fld mt-1" /></div>
          <div><div className="fld-label">Category</div>
            <select value={f.category} onChange={(e) => setF({...f, category: e.target.value})} className="fld mt-1">
              {CATS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div><div className="fld-label">Price</div><input type="number" value={f.price} onChange={(e) => setF({...f, price: e.target.value})} className="fld mt-1" /></div>
          <div><div className="fld-label">Stock</div><input type="number" value={f.stock} onChange={(e) => setF({...f, stock: e.target.value})} className="fld mt-1" /></div>
          <div className="sm:col-span-2"><div className="fld-label">Material</div><input value={f.material} onChange={(e) => setF({...f, material: e.target.value})} className="fld mt-1" /></div>
          <div className="sm:col-span-2"><div className="fld-label">Image URLs (comma separated)</div><input value={f.images} onChange={(e) => setF({...f, images: e.target.value})} className="fld mt-1" /></div>
          <div><div className="fld-label">Sizes</div><input value={f.sizes} onChange={(e) => setF({...f, sizes: e.target.value})} className="fld mt-1" /></div>
          <div><div className="fld-label">Colors</div><input value={f.colors} onChange={(e) => setF({...f, colors: e.target.value})} className="fld mt-1" /></div>
          <div className="sm:col-span-2"><div className="fld-label">Description</div><textarea rows={3} value={f.description} onChange={(e) => setF({...f, description: e.target.value})} className="fld mt-1 resize-none" /></div>
          <label className="flex gap-2 items-center text-xs"><input type="checkbox" checked={f.is_new} onChange={(e) => setF({...f, is_new: e.target.checked})} /> Mark as new</label>
          <label className="flex gap-2 items-center text-xs"><input type="checkbox" checked={f.is_bestseller} onChange={(e) => setF({...f, is_bestseller: e.target.checked})} /> Bestseller</label>
        </div>
        <button data-testid="new-product-save" className="btn-primary mt-8 w-full justify-center">Create product</button>
      </form>
    </div>
  );
}
